export type HistoryActionClassification =
  | "command"
  | "hydrate"
  | "lifecycle"
  | "session"
  | "external-dirty";

const classify = (
  prefix: string,
  classification: HistoryActionClassification,
  names: string[]
) =>
  Object.fromEntries(
    names.map((name) => [prefix + name, classification] as const)
  );

export const HISTORY_ACTION_CLASSIFICATIONS: Record<
  string,
  HistoryActionClassification
> = {
  ...classify("training/", "command", [
    "setTrainingName",
    "setDisableProgressBar",
    "addNewTake",
    "addTakeCopy",
    "addInteractionToTake",
    "addInteractionToSelectedTake",
    "addInteractionCopy",
    "addInteractionToQuizFeedback",
    "reorderQuizFeedbackInteractions",
    "deleteInteraction",
    "setInteractionsDisabled",
    "reorderTakes",
    "reorderInteractions",
    "moveInteraction",
    "moveInteractions",
    "moveSequenceSelection",
    "updateSelectedTake",
    "updateSelectedTakeProperty",
    "updateSelectedInteraction",
    "updateSelectedInteractionProperty",
    "updateSelectedTakeSequence",
    "setReferenceLanguage",
    "replaceTakesFromUnity",
    "deleteTake",
    "addActor",
    "updateActor",
    "deleteActor",
    "addPropPreset",
    "addEnvironmentPropPresetToAllTakes",
    "deletePropPreset",
    "setSelectedEnvironmentLayerIds",
    "updateSelectedActorState",
    "addPropStateToSelectedTake",
    "removePropStateFromSelectedTake",
    "updateSelectedPropPreset",
    "updatePropPresetPrefabNames",
    "addSituationToTakeLocation",
    "removeSituationFromTakeLocation",
    "addAttachmentToTake",
    "removeAttachmentFromTake",
    "matchTakeStartToPreviousTake",
    "setSequenceVariables",
    "addSequenceVariable",
    "updateSequenceVariable",
    "deleteSequenceVariable",
  ]),
  ...classify("training/", "hydrate", [
    "setTrainingDescription",
    "setTrainingType",
    "loadTrainingMetadata",
    "loadDisableProgressBar",
    "loadActors",
    "loadPropPresets",
    "loadSelectedEnvironmentLayerIds",
    "loadSequenceVariables",
    "hydrateTakes",
    "loadReferenceLanguage",
  ]),
  ...classify("training/", "lifecycle", [
    "createNewTraining",
    "clearTakes",
    "clearTraining",
    "resetIsDirty",
  ]),
  ...classify("training/", "session", [
    "setSelectedTake",
    "setSelectedInteraction",
    "toggleInteractionSelection",
    "selectInteractionRange",
    "setPrimaryInteraction",
    "setSelectedInteractions",
    "setSelectedTakeSequence",
    "setIsPlaying",
    "setFilter",
    "setViewportBounds",
    "setImageBounds",
    "setSelectedPropPreset",
    "setSelectedActor",
  ]),
  ...classify("training/", "external-dirty", ["setIsDirty"]),
  ...classify("trainingLocalization/", "command", [
    "registerNewKey",
    "registerNewKeys",
    "registerPropLocalizedKeys",
    "updateLocalizedString",
    "updatePropLocalizedString",
    "applyPropLocalizedTranslations",
    "updateLocalizedStringFromExcel",
    "updateLocalizedRecord",
    "mergeLocalizedEntryValues",
    "addLocalizedLanguage",
    "addAutoTranslatedLanguage",
    "addMissingAutoTranslatedKeys",
    "deleteLocalizedLanguage",
    "deleteKeys",
    "UpdateSetOfKeys",
    "setLanguageIncludeInPublish",
  ]),
  ...classify("trainingLocalization/", "hydrate", [
    "hydrateLocalization",
    "addLocalizedKeys",
    "addSerializedLanguage",
    "addSupportedLanguages",
    "setInitialLanguage",
  ]),
  ...classify("trainingLocalization/", "lifecycle", [
    "clearLocalization",
    "initializeNewLocalization",
  ]),
  ...classify("trainingLocalization/", "session", ["setCurrentLanguage"]),
};

export function getHistoryActionClassification(
  type: string
): HistoryActionClassification | undefined {
  return HISTORY_ACTION_CLASSIFICATIONS[type];
}

export function isUndoableActionType(type: string): boolean {
  return getHistoryActionClassification(type) === "command";
}
